import { buildAiChatSessionId } from './aiChatSession.js'

function fetchInterceptor({
  host = "flow.exatom.io",
  // extraData can be an object or a function (urlObj, request) => object | Promise<object>
  extraData = {},
  // Optional predicate to further refine interception logic
  shouldIntercept, // (urlObj: URL, req: Request) => boolean
  vueRoot
} = {}) {
  const originalFetch = window.fetch;

  window.fetch = async function(input, init) {
    // Build a Request object to normalize both call styles
    const baseReq = input instanceof Request ? input : new Request(input, init);

    try {
      // Resolve absolute URL (handles relative paths)
      const urlObj = new URL(baseReq.url, location.origin);
      const method = (baseReq.method || 'GET').toUpperCase();

      const matchesHost = shouldIntercept
        ? !!shouldIntercept(urlObj, baseReq)
        : urlObj.hostname.endsWith(host);

      const isPost = method === "POST";

      // Only intercept matching POSTs; otherwise pass through unchanged
      if (!matchesHost || !isPost) {
        return originalFetch(input, init);
      }

      const resolvedExtra =
        typeof extraData === "function"
          ? await extraData(urlObj, baseReq)
          : extraData;

      const headers = new Headers(baseReq.headers);
      const clone = baseReq.clone();
      const ct = (headers.get("content-type") || "").toLowerCase();

      let body;

      // Helpers
      const appendKV = (target, data) => {
        for (const [k, v] of Object.entries(data || {})) {
          // FormData can take Blobs/Files; default to string for other types
          if (target instanceof FormData) {
            target.append(k, v instanceof Blob ? v : String(v));
          } else if (target instanceof URLSearchParams) {
            target.set(k, String(v));
          }
        }
      };

      if (ct.includes("application/json")) {
        let json = {};
        try {
          json = await clone.json();
        } catch (_) {
          // ignore parse errors; treat as empty
        }
        body = JSON.stringify({ ...(json || {}), ...(resolvedExtra || {}) });
        headers.set("content-type", "application/json;charset=UTF-8");
      } else if (ct.includes("application/x-www-form-urlencoded")) {
        const params = new URLSearchParams(await clone.text());
        appendKV(params, resolvedExtra);
        body = params.toString();
        headers.set("content-type", "application/x-www-form-urlencoded;charset=UTF-8");
      } else if (ct.includes("multipart/form-data")) {
        const fd = await clone.formData();
        appendKV(fd, resolvedExtra);
        body = fd;
        // Let the browser set the proper boundary
        headers.delete("content-type");
      } else {
        // Try to infer if there's an existing body; then choose a safe merge strategy
        let raw = "";
        try {
          raw = await clone.text();
        } catch (_) {
          // ignore; raw stays ""
        }

        if (raw) {
          // Try JSON merge first
          try {
            const parsed = JSON.parse(raw);
            body = JSON.stringify({ ...(parsed || {}), ...(resolvedExtra || {}) });
            headers.set("content-type", "application/json;charset=UTF-8");
          } catch {
            // Fallback: treat as x-www-form-urlencoded
            const params = new URLSearchParams(raw);
            appendKV(params, resolvedExtra);
            body = params.toString();
            headers.set("content-type", "application/x-www-form-urlencoded;charset=UTF-8");
          }
        } else {
          // No body present; send JSON with the extra data
          body = JSON.stringify(resolvedExtra || {});
          headers.set("content-type", "application/json;charset=UTF-8");
        }
      }

      if (headers.get("content-type").startsWith("application/json")) {
        body = JSON.parse(body);
        // Get the session ID
        body.sessionId = buildAiChatSessionId({
          userId: vueRoot?.me?.id,
          clientHash: vueRoot?.client?.client_hash,
          formHash: vueRoot?.aiChatAdditionalMetadata?.formHash,
          hostname: typeof window !== 'undefined'
            ? window.location.hostname
            : ''
        }, { increment: false });
        body = JSON.stringify(body);
      }

      // Build a new Request so we preserve all original properties (signal, credentials, etc.)
      const newReq = new Request(baseReq, {
        method: "POST",
        headers,
        body,
      });

      return originalFetch(newReq);
    } catch (_) {
      // On any failure, fall back to the original call
      return originalFetch(input, init);
    }
  };

  // Optional: return an uninstall function to restore original fetch
  return function uninstall() {
    window.fetch = originalFetch;
  };
}

export { fetchInterceptor }
